import { FuelAccordionItem, FuelDivider, FuelIcon, FuelTypography } from '@frete.com/fuel-react';
import React, { useMemo, useRef, useState } from 'react';

import { FuelFlex } from '@frete.com/fuel-station-react';
import { DS_TOKENS } from 'constants/styled.contants';
import { addDays, format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useGetRiskManagementLastConsultations } from 'hooks/queries/useGetRiskManagementLastConsultations';
import { useGetTruckerQueriesData } from 'hooks/useGetTruckerQueriesData';
import { isEmpty } from 'utils/validate';
import { BlueStrong } from '../Card/styled';
import { ContentCard } from './ContentCard';
import { Error } from './Error';
import { List } from './List';
import { Skeleton } from './Skeleton';
import * as Styled from './styled';
import { ILastConsultations } from './types';

const LastConsultations: React.FC<ILastConsultations> = ({ totalApproves, isApprovalValid }) => {
  const [isOpen, setIsOpen] = useState(false);
  const accordionRef = useRef(null);
  const { truckerData } = useGetTruckerQueriesData();

  const {
    isLoading: isLoadingLastConsultations,
    isFetching: isFetchingLastConsultations,
    isError: isErrorLastConsultations,
    isLoadingError: isLoadingErrorLastConsultations,
    isRefetchError: isRefetchErrorLastConsultations,
    isFetched: isFetchedLastConsultations,
    refetch: refetchLastConsultations,
    data: lastConsultationsData,
  } = useGetRiskManagementLastConsultations(truckerData?.trucker?.id);

  const isLoading = useMemo(
    () => isLoadingLastConsultations || isFetchingLastConsultations,
    [isLoadingLastConsultations, isFetchingLastConsultations]
  );

  const isError = useMemo(
    () =>
      (isErrorLastConsultations || isLoadingErrorLastConsultations || isRefetchErrorLastConsultations) && !isLoading,
    [isErrorLastConsultations, isLoadingErrorLastConsultations, isRefetchErrorLastConsultations, isLoading]
  );

  const isFetched = useMemo(
    () => (isFetchedLastConsultations || !isEmpty(lastConsultationsData?.data)) && !isError && !isLoading,
    [isFetchedLastConsultations, isError, isLoading, LastConsultations]
  );

  /* istanbul ignore next */
  const handleToggleAccordion = () => accordionRef?.current?.toggle();

  const formattedRegistrationValidity =
    truckerData?.lastSearches?.lastApprovedDate &&
    format(addDays(parseISO(truckerData?.lastSearches?.lastApprovedDate), 180), 'dd/MM/yyyy', { locale: ptBR });

  return (
    <FuelAccordionItem
      ref={accordionRef}
      onFuel-accordion-close={/* istanbul ignore next */ () => setIsOpen(false)}
      onFuel-accordion-open={/* istanbul ignore next */ () => setIsOpen(true)}
      data-testid="last-consultations-accordion"
    >
      <FuelFlex
        slot="header"
        onClick={handleToggleAccordion}
        defaultProps={{ justifyContent: 'space-between', gap: 'km3', width: '100%', alignItems: 'center' }}
      >
        <FuelFlex defaultProps={{ justifyContent: 'flex-start', gap: 'km3' }}>
          <FuelIcon size="small" name="user-area" iconStyle="filled" data-testid="user-area-accordion-icon" />
          <FuelTypography weight="medium" size="body-md" color="neutral-strong" data-testid="test-accordion-header">
            {totalApproves === 1 ? (
              <>
                {totalApproves} consulta e cadastro <BlueStrong>aprovada</BlueStrong>
              </>
            ) : (
              <>
                {totalApproves} consultas e cadastros <BlueStrong>aprovadas</BlueStrong>
              </>
            )}
          </FuelTypography>
        </FuelFlex>

        <FuelFlex defaultProps={{ alignItems: 'center', justifyContent: 'center', gap: 'km1' }}>
          <FuelTypography size="caption" weight="bold">
            {/* istanbul ignore next */ isOpen ? 'Ver menos' : 'Ver mais'}
          </FuelTypography>
          <Styled.ChevronWrapper isOpen={isOpen}>
            <FuelIcon name="chevron-down" size="medium" data-testid="chevron-down-accordion-icon" />
          </Styled.ChevronWrapper>
        </FuelFlex>
      </FuelFlex>

      <FuelFlex
        slot="content"
        defaultProps={{
          flexDirection: 'column',
          customPadding: `${DS_TOKENS.spacing.km4} 0 0 0`,
          alignItems: 'flex-start',
        }}
      >
        {isLoading && (
          <List>
            <Skeleton />
          </List>
        )}
        {isError && <Error refetch={refetchLastConsultations} />}
        {isFetched && (
          <List>
            {lastConsultationsData?.data?.map((consultation) => (
              <ContentCard {...consultation} key={`${consultation.company.id}-${consultation.grDate}`} />
            ))}
          </List>
        )}
        {isApprovalValid ? (
          <FuelTypography size="caption" weight="regular" color="neutral-default" spacingTop={8} spacingBottom={8}>
            Cadastro válido para consulta até {formattedRegistrationValidity}
          </FuelTypography>
        ) : (
          <FuelTypography size="caption" weight="regular" color="neutral-default" spacingTop={8} spacingBottom={8}>
            Cadastro perdeu validade para consulta em {formattedRegistrationValidity}
          </FuelTypography>
        )}
        {isFetched && (
          <FuelFlex defaultProps={{ width: '100%', flexDirection: 'column' }}>
            <FuelDivider orientation="horizontal" />
          </FuelFlex>
        )}
      </FuelFlex>
    </FuelAccordionItem>
  );
};

export default LastConsultations;
